import React, { useEffect, useMemo, useRef, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  IconButton,
  Box,
  MenuItem,
  Popover,
  Tooltip,
  MenuList,
  Typography,
  Link,
  SwipeableDrawer,
  Grid,
  Checkbox,
} from "@mui/material";
import OutlineIconButton from "../components/OutlineIconButton";
import {
  Constants,
  useMediaDevice,
  useMeeting,
  usePubSub,
  useTranscription,
  createCameraVideoTrack
} from "@videosdk.live/react-sdk";
import {
  sideBarModes,
  appThemes,
  useMeetingAppContext,
  sideBarNestedModes,
  meetingLayoutTopics,
} from "../MeetingAppContextDef";
import useIsTab from "../utils/useIsTab";
import useIsMobile from "../utils/useIsMobile";
import recordingBlink from "../animations/recording-blink.json";
import liveBlink from "../animations/live-blink.json";
import liveHLS from "../animations/live-hls.json";
import liveCC from "../animations/live-cc.json";
import stoppingHLS from "../animations/hls_stop_blink.json";
import LiveIcon from "../icons/LiveIcon";
import RaiseHand from "../icons/RaiseHand";
import {
  Activities,
  Chat,
  EndCall,
  Participants,
  ScreenRecording,
  ScreenShare,
  LeaveMeetingIcon,
  EndCallIcon,
  MicOn,
} from "../icons";
import {
  MoreHoriz as MoreHorizIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Gesture,
  ClosedCaption,
  ClosedCaptionOutlined,
} from "@mui/icons-material";
import ConfirmBox from "../components/ConfirmBox";
import OutlineIconTextButton from "../components/OutlineIconTextButton";
import MobileIconButton from "../components/MobileIconButton";
import AddLiveStreamIcon from "../icons/AddLiveStreamIcon";
import useIsLivestreaming from "./useIsLivestreaming";
import useIsRecording from "./useIsRecording";
import useIsHls from "./useIsHls";
import { meetingModes } from "../CONSTS";
import ConfigIcon from "../icons/ConfigIcon";
import MicOffIcon from "../icons/MicOffIcon";
import MicrophoneIcon from "../icons/MicrophoneIcon";
import SpeakerMenuIcon from "../icons/SpeakerMenuIcon";
import SelectedIcon from "../icons/SelectedIcon";
import { useSnackbar } from "notistack";
import { VideoSDKNoiseSuppressor } from "@videosdk.live/videosdk-media-processor-web";
import useCustomTrack from "../utils/useCustomTrack";
import useIsTranscriptionRunning from "./useIsTranscriptionRunning";
import { useLocation } from "react-router-dom";
import { createShow } from "../../Services/ShowsApi";
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded';
import { createMeetingBanner, createMeetingLogo } from "../../Services/BannersApi";
import { addNoParticipantStartTime } from "../../Services/MeetingApi";
// import { createMeetingBanner } from "../../Services/BannersApi";
import { VirtualBackgroundProcessor } from "@videosdk.live/videosdk-media-processor-web";
import { debounce } from "../utils/common";
import { useDispatch } from "react-redux";
import { setIsRecorderPaused } from "../../redux/recorderSlice";

const CustomBox = styled(Box)`
  &:hover {
    background: #2b303499;
  }
`;

const CustomBoxLight = styled(Box)`
  &:hover {
    background: #ccd2d899;
  }
`;
const CustomBoxDefault = styled(Box)`
  &:hover {
    background: #43425399;
  }
`;

const CustomMenuItem = styled(MenuItem)`
  &:hover {
    background: transparent;
  }
  &.MuiMenuItem-gutters {
    padding: 6px 28px;
  }
  &.Mui-selected {
    padding: 6px 12px;
  }
`;

const CustomWebcamMenuItem = styled(MenuItem)`
  &:hover {
    background: transparent !important;
  }
  &.MuiMenuItem-gutters {
    padding: 6px 18px;
  }
  &.Mui-selected {
    padding: 6px 18px;
    background: transparent;
  }
`;

const CustomIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 3px;
  }
`;

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: 12,
  padding: 0,
  margin: 0,
  width: 10,
  height: 10,
  border: `2px solid ${theme.palette.text.secondary}`,
  "input:disabled ~ &": {
    boxShadow: "none",
    background: theme.palette.text.secondary,
  },
}));

const BpCheckedIcon = styled(SelectedIcon)({
  "&:before": {
    display: "block",
    width: 10,
    height: 10,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
});

const BpCheckedLightIcon = styled(SelectedIcon)({
  "&:before": {
    display: "block",
    width: 10,
    height: 10,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
});

function BpCheckbox(CheckboxProps) {
  const { appTheme } = useMeetingAppContext();
  return (
    <Checkbox
      disableRipple
      disableFocusRipple
      color="default"
      checkedIcon={
        appTheme === appThemes.LIGHT || appTheme === appThemes.DARK ? (
          <BpCheckedLightIcon />
        ) : (
          <BpCheckedIcon />
        )
      }
      icon={<BpIcon />}
      style={{
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        backgroundColor: "transparent",
      }}
      {...CheckboxProps}
    />
  );
}

// const useStyles = makeStyles({
//   row: { display: "flex", alignItems: "center" },
//   borderRight: { borderRight: "1ps solid #ffffff33" },
//   popover: { backgroundColor: "transparent" },
//   popoverBorder: {
//     borderRadius: "12px",
//     backgroundColor: "#212032",
//     marginTop: 8,
//     width: 300,
//   },
//   popoverHover: {
//     "&:hover": {
//       backgroundColor: "#CCD2D899",
//     },
//   },
//   popoverHoverDark: {
//     "&:hover": {
//       backgroundColor: "#2B303499",
//     },
//   },
//   popoverHoverDefault: {
//     "&:hover": {
//       backgroundColor: "#43425399",
//     },
//   },
//   menuItemHover: {
//     "&:hover": {
//       backgroundColor: "transparent",
//     },
//   },
//   menuItemDark: {
//     "&:hover": {
//       backgroundColor: "transparent",
//     },
//   },
//   menuItemDefault: {
//     "&:hover": {
//       backgroundColor: "transparent",
//     },
//   },
//   singleMenuItemGutters: {
//     padding: "6px 2px",
//   },
//   singleMenuItemGuttersAfterSelect: {
//     padding: "6px 12px",
//   },
//   menuItemGutters: {
//     padding: "6px 28px",
//   },
//   menuItemGuttersAfterSelect: {
//     padding: "6px 12px",
//   },
// });

const RaiseHandBTN = ({ onClick, isMobile, isTab }) => {
  const { publish } = usePubSub("RAISE_HAND");
  // const classes = useStyles();
  const onRaiseHand = () => {
    if (isMobile || isTab) {
      onClick();
      typeof onClick === "function" && onClick();
      publish("Raise Hand");
    } else {
      publish("Raise Hand");
    }
  };

  return isMobile || isTab ? (
    <Tooltip>
      <MobileIconButton
        id="RaiseHandBTN"
        tooltipTitle={"Raise hand"}
        Icon={RaiseHand}
        onClick={onRaiseHand}
        buttonText={"Raise Hand"}
      />
    </Tooltip>
  ) : (
    <Tooltip>
      <OutlineIconButton
        tooltipTitle={"Raise hand"}
        Icon={RaiseHand}
        onClick={onRaiseHand}
      />
    </Tooltip>
  );
};
const ParticipantsBTN = ({ onClick, isMobile, isTab }) => {
  const {
    sideBarMode,
    setSideBarMode,
    meetingMode,
    canToggleParticipantTab,
    appTheme,
  } = useMeetingAppContext();

  const mMeeting = useMeeting();
  const theme = useTheme();
  const participants = mMeeting?.participants;
  const participantsCount = participants ? new Map(participants).size : 0;

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle={"Participants"}
      isFocused={sideBarMode === sideBarModes.PARTICIPANTS}
      buttonText={"Participants"}
      disabledOpacity={1}
      Icon={Participants}
      disabled={meetingMode === meetingModes.VIEWER || !canToggleParticipantTab}
      onClick={() => {
        typeof onClick === "function" && onClick();
        setSideBarMode((s) =>
          s === sideBarModes.PARTICIPANTS ? null : sideBarModes.PARTICIPANTS
        );
      }}
      badge={participantsCount}
    />
  ) : (
    <OutlineIconButton
      tooltipTitle={"Participants"}
      isFocused={sideBarMode === sideBarModes.PARTICIPANTS}
      Icon={Participants}
      disabledOpacity={1}
      focusBGColor={
        appTheme === appThemes.LIGHT && "#95959E"
      }
      disabled={meetingMode === meetingModes.VIEWER || !canToggleParticipantTab}
      onClick={() => {
        typeof onClick === "function" && onClick();
        setSideBarMode((s) =>
          s === sideBarModes.PARTICIPANTS ? null : sideBarModes.PARTICIPANTS
        );
      }}
      badge={participantsCount}
    />
  );
};
const ConfigBTN = ({ isMobile, isTab }) => {
  const { sideBarMode, setSideBarMode, appTheme, setSideBarNestedMode } =
    useMeetingAppContext();
  const theme = useTheme();

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle={"Configuration"}
      buttonText={"Configuration"}
      Icon={ConfigIcon}
      isFocused={sideBarMode === sideBarModes.CONFIGURATION}
      onClick={() => {
        setSideBarNestedMode(null);
        setSideBarMode((s) =>
          s === sideBarModes.CONFIGURATION ? null : sideBarModes.CONFIGURATION
        );
      }}
    />
  ) : (
    <OutlineIconButton
      tooltipTitle={"Configuration"}
      Icon={ConfigIcon}
      focusBGColor={
        appTheme === appThemes.LIGHT && "#95959E"
      }
      isFocused={sideBarMode === sideBarModes.CONFIGURATION}
      onClick={() => {
        setSideBarNestedMode(null);
        setSideBarMode((s) =>
          s === sideBarModes.CONFIGURATION ? null : sideBarModes.CONFIGURATION
        );
      }}
    />
  );
};
const ChatBTN = ({ isMobile, isTab }) => {
  const { sideBarMode, setSideBarMode, appTheme, setSideBarNestedMode } =
    useMeetingAppContext();
  const theme = useTheme();

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle={"Chat"}
      buttonText={"Chat"}
      Icon={Chat}
      isFocused={sideBarMode === sideBarModes.CHAT}
      onClick={() => {
        setSideBarNestedMode(null);
        setSideBarMode((s) =>
          s === sideBarModes.CHAT ? null : sideBarModes.CHAT
        );
      }}
    />
  ) : (
    <OutlineIconButton
      tooltipTitle={"Chat"}
      Icon={Chat}
      focusBGColor={
        appTheme === appThemes.LIGHT && "#95959E"
      }
      isFocused={sideBarMode === sideBarModes.CHAT}
      onClick={() => {
        setSideBarNestedMode(null);
        setSideBarMode((s) =>
          s === sideBarModes.CHAT ? null : sideBarModes.CHAT
        );
      }}
    />
  );
};
const ActivitiesBTN = ({ onClick, isMobile, isTab }) => {
  const { sideBarMode, setSideBarMode, setSideBarNestedMode, appTheme } =
    useMeetingAppContext();
  const theme = useTheme();

  return isMobile || isTab ? (
    <MobileIconButton
      Icon={Activities}
      tooltipTitle={"More Options"}
      buttonText={"More Options"}
      isFocused={sideBarMode === sideBarModes.ACTIVITIES}
      onClick={() => {
        typeof onClick === "function" && onClick();

        setSideBarMode((s) =>
          s === sideBarModes.ACTIVITIES ? null : sideBarModes.ACTIVITIES
        );

        setSideBarNestedMode(null);
      }}
    />
  ) : (
    <OutlineIconButton
      tooltipTitle={"More Options"}
      Icon={Activities}
      focusBGColor={
        appTheme === appThemes.LIGHT && "#95959E"
      }
      isFocused={sideBarMode === sideBarModes.ACTIVITIES}
      onClick={() => {
        typeof onClick === "function" && onClick();

        setSideBarMode((s) =>
          s === sideBarModes.ACTIVITIES ? null : sideBarModes.ACTIVITIES
        );
        setSideBarNestedMode(null);
      }}
    />
  );
};
const WhiteBoardBTN = ({ onClick, isMobile, isTab }) => {
  const {
    whiteboardStarted,
    whiteboardEnabled,
    canToggleWhiteboard,
    appTheme,
  } = useMeetingAppContext();
  const theme = useTheme();

  const mMeeting = useMeeting({});

  const presenterId = mMeeting?.presenterId;

  return (
    <>
      {whiteboardEnabled &&
        (isMobile || isTab ? (
          <MobileIconButton
            disabled={presenterId || !canToggleWhiteboard}
            tooltipTitle={"Whiteboard"}
            buttonText={"Whiteboard"}
            Icon={Gesture}
            isFocused={whiteboardStarted}
            focusIconColor={
              appTheme === appThemes.LIGHT &&
              "#95959E"
            }
            onClick={() => {
              typeof onClick === "function" && onClick();

              whiteboardStarted
                ? mMeeting.meeting.stopWhiteboard()
                : mMeeting.meeting.startWhiteboard();
            }}
          />
        ) : (
          <OutlineIconButton
            disabled={presenterId || !canToggleWhiteboard}
            tooltipTitle={"Whiteboard"}
            Icon={Gesture}
            isFocused={whiteboardStarted}
            focusBGColor={
              appTheme === appThemes.LIGHT &&
              "#95959E"
            }
            onClick={() => {
              typeof onClick === "function" && onClick();

              whiteboardStarted
                ? mMeeting.meeting.stopWhiteboard()
                : mMeeting.meeting.startWhiteboard();
            }}
          />
        ))}
    </>
  );
};
const ScreenShareBTN = ({ onClick, isMobile, isTab }) => {
  const mMeeting = useMeeting({});
  const {
    whiteboardStarted,
    appTheme,
    notificationSoundEnabled,
    notificationAlertsEnabled,
  } = useMeetingAppContext();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();

  const localScreenShareOn = mMeeting?.localScreenShareOn;
  const presenterId = mMeeting?.presenterId;
  const presenterIdRef = useRef(presenterId);

  useEffect(() => {
    presenterIdRef.current = presenterId;
  }, [presenterId]);

  const { getCustomScreenShareTrack } = useCustomTrack();

  const toggleScreenShare = async (screenShareStatus) => {
    let track;
    if (!localScreenShareOn) track = await getCustomScreenShareTrack();

    if (presenterIdRef.current && !localScreenShareOn) {
      let participantName = null;
      mMeeting.participants.forEach((participant) => {
        if (participant.id === presenterIdRef.current) {
          participantName = participant.displayName;
        }
      });

      // if (notificationSoundEnabled) {
      //   new Audio(
      //     `https://static.videosdk.live/prebuilt/notification.mp3`
      //   ).play();
      // }

      if (notificationAlertsEnabled) {
        enqueueSnackbar(
          `Screen sharing unavailable: ${participantName} is currently presenting.`
        );
      }
    } else {
      console.log("screenShareStatus", screenShareStatus);
      mMeeting?.toggleScreenShare(track);
    }
  };

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle={
        presenterId
          ? localScreenShareOn
            ? "Stop Presenting"
            : null
          : "Present Screen"
      }
      buttonText={
        presenterId
          ? localScreenShareOn
            ? "Stop Presenting"
            : null
          : "Present Screen"
      }
      isFocused={localScreenShareOn}
      Icon={ScreenShare}
      onClick={async () => {
        await toggleScreenShare(presenterId
          ? localScreenShareOn
            ? "Stop"
            : null
          : "Start");
        typeof onClick === "function" && onClick();
      }}
      disabled={
        isMobile || isTab
          ? true
          : whiteboardStarted
            ? true
            : presenterId
              ? localScreenShareOn
                ? false
                : true
              : false
      }
    />
  ) : (
    <OutlineIconButton
      tooltipTitle={
        presenterId
          ? localScreenShareOn
            ? "Stop Presenting"
            : null
          : "Present Screen"
      }
      isFocused={localScreenShareOn}
      focusBGColor={
        appTheme === appThemes.LIGHT && "#95959E"
      }
      Icon={ScreenShare}
      onClick={() => {
        typeof onClick === "function" && onClick();
        toggleScreenShare(presenterId
          ? localScreenShareOn
            ? "Stop"
            : null
          : "Start");
      }}
      disabled={
        isMobile || isTab
          ? true
          : whiteboardStarted
            ? true
            : presenterId
              ? localScreenShareOn
                ? false
                : true
              : false
      }
    />
  );
};
const AddLiveStreamBTN = ({ isMobile, isTab }) => {
  const { sideBarMode, setSideBarMode } = useMeetingAppContext();

  return isMobile || isTab ? (
    <MobileIconButton
      tooltipTitle={"Add Live Streams"}
      Icon={AddLiveStreamIcon}
      buttonText={"Add Live Streams"}
      isFocused={sideBarMode === sideBarModes.ADD_LIVE_STREAM}
      onClick={() => {
        setSideBarMode((s) =>
          s === sideBarModes.ADD_LIVE_STREAM
            ? null
            : sideBarModes.ADD_LIVE_STREAM
        );
      }}
    />
  ) : (
    <OutlineIconTextButton
      tooltipTitle={"Add Live Streams"}
      buttonText="Add Live Streams"
      isFocused={sideBarMode === sideBarModes.ADD_LIVE_STREAM}
      onClick={() => {
        setSideBarMode((s) =>
          s === sideBarModes.ADD_LIVE_STREAM
            ? null
            : sideBarModes.ADD_LIVE_STREAM
        );
      }}
    />
  );
};
const TranscriptionBTN = ({ isMobile, isTab }) => {
  const { startTranscription, stopTranscription } = useTranscription();
  const mMeeting = useMeeting({});
  const theme = useTheme();
  const transcriptionState = mMeeting?.transcriptionState;

  const isTranscriptionRunning = useIsTranscriptionRunning();
  const { participantCanToggleRealtimeTranscription, appTheme } =
    useMeetingAppContext();
  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        transcriptionState ===
        Constants.transcriptionEvents.TRANSCRIPTION_STARTING ||
        transcriptionState ===
        Constants.transcriptionEvents.TRANSCRIPTION_STOPPING,
    }),
    [transcriptionState]
  );
  const isTranscriptionRunningRef = useRef(isTranscriptionRunning);

  useEffect(() => {
    isTranscriptionRunningRef.current = isTranscriptionRunning;
  }, [isTranscriptionRunning]);

  const _handleClick = () => {
    const isTranscriptionRunning = isTranscriptionRunningRef.current;

    if (isTranscriptionRunning) {
      stopTranscription();
    } else {
      startTranscription();
    }
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: liveCC,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
    iconScale: "scale(+5)",
    height: 70,
    width: 100,
  };
  return (
    <>
      {isMobile || isTab ? (
        <MobileIconButton
          Icon={
            transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STARTED
              ? ClosedCaption
              : ClosedCaptionOutlined
          }
          onClick={_handleClick}
          tooltipTitle={
            transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STARTED
              ? "Stop Transcription"
              : transcriptionState ===
                Constants.transcriptionEvents.TRANSCRIPTION_STARTING
                ? "Starting Transcription"
                : transcriptionState ===
                  Constants.transcriptionEvents.TRANSCRIPTION_STOPPED
                  ? "Start Transcription"
                  : transcriptionState ===
                    Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
                    ? "Stopping Transcription"
                    : "Start Transcription"
          }
          isFocused={isTranscriptionRunning}
          disabled={!participantCanToggleRealtimeTranscription}
          bgColor={
            appTheme === appThemes.LIGHT &&
            (transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STARTED ||
              transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STOPPING) &&
            "#EEF0F2"
          }
          buttonText={
            transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STARTED
              ? "Stop Transcription"
              : transcriptionState ===
                Constants.transcriptionEvents.TRANSCRIPTION_STARTING
                ? "Starting Transcription"
                : transcriptionState ===
                  Constants.transcriptionEvents.TRANSCRIPTION_STOPPED
                  ? "Start Transcription"
                  : transcriptionState ===
                    Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
                    ? "Stopping Transcription"
                    : "Start Transcription"
          }
          isRequestProcessing={isRequestProcessing}
        />
      ) : (
        <OutlineIconTextButton
          onClick={_handleClick}
          buttonText={
            transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STARTED
              ? "CC"
              : transcriptionState ===
                Constants.transcriptionEvents.TRANSCRIPTION_STARTING
                ? "CC"
                : transcriptionState ===
                  Constants.transcriptionEvents.TRANSCRIPTION_STOPPED
                  ? "CC"
                  : transcriptionState ===
                    Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
                    ? "CC"
                    : "CC"
          }
          tooltipTitle={
            transcriptionState ===
              Constants.transcriptionEvents.TRANSCRIPTION_STARTED
              ? "Stop Transcription"
              : transcriptionState ===
                Constants.transcriptionEvents.TRANSCRIPTION_STARTING
                ? "Starting Transcription"
                : transcriptionState ===
                  Constants.transcriptionEvents.TRANSCRIPTION_STOPPED
                  ? "Start Transcription"
                  : transcriptionState ===
                    Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
                    ? "Stopping Transcription"
                    : "Start Transcription"
          }
          isFocused={isTranscriptionRunning}
          lottieOption={
            transcriptionState ==
              Constants.transcriptionEvents.TRANSCRIPTION_STARTING
              ? defaultOptions
              : null
          }
          disabled={!participantCanToggleRealtimeTranscription}
          isRequestProcessing={isRequestProcessing}
        />
      )}
    </>
  );
};

const RecPauseBTN = () => {
  const [isPaused, setIsPaused] = useState(false);
  const { publish } = usePubSub("REC_PAUSE");
  const hanldlePauseClick = () => {
    const nextPauseState = !isPaused;
    publish({ recPause: nextPauseState });
    setIsPaused(nextPauseState);
  }
  return (
    <div>
      <button onClick={hanldlePauseClick}> {isPaused ? "Resume" : "Pause"}</button>
    </div>
  );
};

const RecordingBTN = ({ isMobile, isTab }) => {
  const dispatch = useDispatch()
  const mMeeting = useMeeting({});
  const location = useLocation();  // Using useLocation hook to access the URL
  const theme = useTheme();
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const startRecording = mMeeting?.startRecording;
  const stopRecording = mMeeting?.stopRecording;
  const recordingState = mMeeting?.recordingState;

  const isRecording = useIsRecording();

  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        recordingState === Constants.recordingEvents.RECORDING_STARTING ||
        recordingState === Constants.recordingEvents.RECORDING_STOPPING,
    }),
    [recordingState]
  );

  const {
    recordingWebhookUrl,
    recordingAWSDirPath,
    participantCanToggleRecording,
    appMeetingLayout,
    recordingTheme,
    appTheme,
  } = useMeetingAppContext();

  const { type, priority, gridSize } = useMemo(
    () => ({
      type: appMeetingLayout.type,
      priority: appMeetingLayout.priority,
      gridSize: appMeetingLayout.gridSize,
    }),
    [appMeetingLayout]
  );

  const typeRef = useRef(type);
  const priorityRef = useRef(priority);
  const gridSizeRef = useRef(gridSize);
  const isRecordingRef = useRef(isRecording);

  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  useEffect(() => {
    priorityRef.current = priority;
  }, [priority]);

  useEffect(() => {
    gridSizeRef.current = gridSize;
  }, [gridSize]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: recordingBlink,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
    height: 64,
    width: 160,
  };

  const [latestMessage, setLatestMessage] = useState(null);
  const [latestLogo, setLatestLogo] = useState(null);
  usePubSub("UPDATE_BANNER", {
    onMessageReceived: (data) => {
      const { message } = data;
      setLatestMessage(message);
    },
    onOldMessagesReceived: (messages) => {
      console.log("Old Message published with persist:true Received", messages);
      // Check if any old message exists, and if so, set it to the state
      if (messages && messages.length > 0) {
        const latestMessage = messages[messages.length - 1].message; // Get the most recent banner message
        setLatestMessage(latestMessage);
      }
    },
  });

  usePubSub("UPDATE_LOGO", {
    onMessageReceived: (data) => {
      const { message } = data;
      setLatestLogo(message);
    },
    onOldMessagesReceived: (messages) => {
      console.log("Old Message published with persist:true Received", messages);
      // Check if any old message exists, and if so, set it to the state
      if (messages && messages.length > 0) {
        const latestMessage = messages[messages.length - 1].message; // Get the most recent banner message
        setLatestLogo(latestMessage);
      }
    },
  });

  const _handleStartRecording = async () => {

    // Parse query parameters to get show name
    const queryParams = new URLSearchParams(location.search);
    const showName = queryParams.get("showName");
    const meetingId = queryParams.get("meetingId");
    const token = queryParams.get("token");
    try {
      const type = typeRef.current;
      const priority = priorityRef.current;
      const gridSize = gridSizeRef.current;

      // const layout = { type, priority, gridSize };
      // const templateUrl = `https://dyrect-dev.ai/custombanner?meetingId=${meetingId}&token=${token}`
      // const transcription = {
      //   enabled: true, // Enables post transcription
      //   summary: {
      //     enabled: true,
      //     prompt:
      //       "Write summary in sections like Speakers, Outlines and Summary",
      //   },
      // };
      // Start the recording with the layout and theme
      // startRecording(recordingWebhookUrl, recordingAWSDirPath, {
      //   layout,
      //   theme: recordingTheme,
      //   transcription: transcription,
      //   // templateUrl: templateUrl,
      //   quality: "high"
      // }, transcription);

      // await startRecordingCustom(recordingWebhookUrl, meetingId, token);
      // await new Promise(resolve => setTimeout(resolve, 5000));
      await startRecordingCustom(recordingWebhookUrl, meetingId, token);

      const showDetailsData = {
        UserID: localStorage.getItem("user_id"),
        ShowName: showName,
        RoomID: meetingId, // Ensure meetingId is defined
      };

      console.log("showDetailsData", showDetailsData);

      // Call createShow function
      await createShow(showDetailsData);
      console.log(latestMessage);
      if (latestMessage?.BannerID !== null) {
        const createBannerData = {
          BannerID: latestMessage?.BannerID,
          MeetingID: meetingId,
          BackgroundColor: latestMessage.backgroundColor,
          FontSize: `${latestMessage?.fontSize}`,
          StartTime: getCurrentTimestamp()
        };
        console.log('createBannerData', createBannerData);
        await createMeetingBanner(createBannerData)
      }
      if (latestLogo?.LogoId !== null) {
        const createMeetingLogoData = {
          LogoID: latestLogo.LogoId,
          MeetingID: meetingId,
          StartTime: getCurrentTimestamp()
        }
        console.log('createMeetingLogoData', createMeetingLogoData);
        await createMeetingLogo(createMeetingLogoData);
      }
      if (mMeeting.pinnedParticipants.size === 0) {
        const data = {
          MeetingID: meetingId,
          StartTime: getCurrentTimestamp()
        }
        await addNoParticipantStartTime(data);
      }

    } catch (error) {
      console.error("Error in _handleStartRecording:", error);
    }
  };

  const startRecordingCustom = async (recordingWebhookUrl, mId, token) => {
    const url = "https://api.videosdk.live/v2/recordings/start";


    // const parsedUrl = new URL(window.location.href);
    // const dyrectBaseUrl = `${parsedUrl.protocol}//${parsedUrl.host}`;
    // console.log('dyrectBaseUrl', dyrectBaseUrl);
    const parsedUrl = new URL(window.location.href);
    const dyrectBaseUrl = parsedUrl.hostname === "localhost"
      ? "https://dyrect-dev.ai"
      : `${parsedUrl.protocol}//${parsedUrl.host}`;

    // Replace with your actual token
    // const YOUR_VIDEOSDK_SERVER_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhcGlrZXkiOiI4YmNiNjdkZi1jMDU3LTQ2NTEtYTA2ZS03ZTA0ZmIxZTkzYjMiLCJwZXJtaXNzaW9ucyI6WyJhbGxvd19qb2luIiwiYWxsb3dfbW9kIl0sInJvb21JZCI6InFkd3ItanRiNC15MnYxIiwiaWF0IjoxNzM4Mjc1MjczfQ.YI7GHIsFByS02HeCk5GwSK7T2Czn5PtpCqOtO0S-mGc";
    const meetingId = mId;
    const transcription = {
      enabled: true, // Enables post transcription
      summary: {
        enabled: true,
        // prompt:
        //   "Write summary in sections like Speakers, Outlines and Summary.",
        prompt:
          "If there is less than 100 characters, say 'No audio was detected for this podcast, therefore no summary was created.' Else, Write summary in sections like Speakers, Outlines and Summary. ",
      },
    };
    const options = {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        roomId: await getRoomId(mId, token),
        templateUrl: `${dyrectBaseUrl}/meeting/?guest=true&meetingId=${mId}&name=recording&token=${token}&joinWithoutUserInteraction=true&isRecorder=true&harkEnabled=true&animationsEnabled=false&realtimeTranscriptionVisible=false`,
        config: {
          quality: "high",
          layout: {
            type: "GRID",
            priority: "SPEAKER"
          },
        },
        // webhookUrl: 'https://j3u63mt0oe.execute-api.us-east-1.amazonaws.com/dev/banners',
        awsDirPath: `video-sdk-recording/${meetingId}`,
        transcription: transcription
      }),
    };

    try {
      const response = await fetch(url, options);

      // Debug: Check status code and raw response text
      const rawResponse = await response.text();
      console.log("Raw Response:", rawResponse);


      // Attempt to parse JSON if the response is valid
      let data;
      try {
        data = JSON.parse(rawResponse);
      } catch (jsonError) {
        console.error("Failed to parse JSON response:", jsonError.message);
      }

      console.log("Start Recording API Response:", data);
    } catch (error) {
      console.error("Error calling start recording API:", error.message);
    }
  }

  const getRoomId = async (mId, token) => {
    const url = `https://api.videosdk.live/v2/rooms/${mId}`;
    const options = {
      method: "GET",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json(); // Parse the response body as JSON
      console.log('Custom room ID response:', data); // Log the full response data
      return data.roomId; // Access the roomId from the parsed data
    } catch (error) {
      console.error("Error fetching room ID:", error);
    }
  };


  function getCurrentTimestamp() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const _handleClick = () => {
    const isRecording = isRecordingRef.current;

    if (isRecording) {
      stopRecording();
    } else {
      setShowConfirmationPopup(true);
    }
  };

  return (
    <>
      {isMobile || isTab ? (
        <MobileIconButton
          Icon={ScreenRecording}
          onClick={_handleClick}
          tooltipTitle={
            recordingState === Constants.recordingEvents.RECORDING_STARTED
              ? "Stop Recording"
              : recordingState === Constants.recordingEvents.RECORDING_STARTING
                ? "Starting Recording"
                : recordingState === Constants.recordingEvents.RECORDING_STOPPED
                  ? "Start Recording"
                  : recordingState === Constants.recordingEvents.RECORDING_STOPPING
                    ? "Stopping Recording"
                    : "Start Recording"
          }
          isFocused={isRecording}
          disabled={!participantCanToggleRecording}
          lottieOption={isRecording ? defaultOptions : null}
          bgColor={
            appTheme === appThemes.LIGHT &&
            (recordingState === Constants.recordingEvents.RECORDING_STARTED ||
              recordingState ===
              Constants.recordingEvents.RECORDING_STOPPING) &&
            "#EEF0F2"
          }
          buttonText={
            recordingState === Constants.recordingEvents.RECORDING_STARTED
              ? "Stop Recording"
              : recordingState === Constants.recordingEvents.RECORDING_STARTING
                ? "Starting Recording"
                : recordingState === Constants.recordingEvents.RECORDING_STOPPED
                  ? "Start Recording"
                  : recordingState === Constants.recordingEvents.RECORDING_STOPPING
                    ? "Stopping Recording"
                    : "Start Recording"
          }
          isRequestProcessing={isRequestProcessing}
        />
      ) : (
        <div style={{ display: 'flex' }}>
          <OutlineIconButton
            Icon={ScreenRecording}
            onClick={_handleClick}
            focusBGColor={
              appTheme === appThemes.LIGHT &&
              "#95959E"
            }
            tooltipTitle={
              recordingState === Constants.recordingEvents.RECORDING_STARTED
                ? "Stop Recording"
                : recordingState === Constants.recordingEvents.RECORDING_STARTING
                  ? "Starting Recording"
                  : recordingState === Constants.recordingEvents.RECORDING_STOPPED
                    ? "Start Recording"
                    : recordingState === Constants.recordingEvents.RECORDING_STOPPING
                      ? "Stopping Recording"
                      : "Start Recording"
            }
            isFocused={
              isRecording &&
              recordingState === Constants.recordingEvents.RECORDING_STARTED
            }
            bgColor={
              appTheme === appThemes.LIGHT &&
              (recordingState === Constants.recordingEvents.RECORDING_STARTED ||
                recordingState ===
                Constants.recordingEvents.RECORDING_STOPPING) &&
              "#EEF0F2"
            }
            disabled={!participantCanToggleRecording}
            lottieOption={
              isRecording &&
                recordingState === Constants.recordingEvents.RECORDING_STARTED
                ? defaultOptions
                : null
            }
            buttonText={
              recordingState === Constants.recordingEvents.RECORDING_STARTING
                ? "Starting"
                : recordingState === Constants.recordingEvents.RECORDING_STOPPING
                  ? "Stopping"
                  : ""
            }
            isRequestProcessing={isRequestProcessing}
          />
          {/* <RecPauseBTN /> */}
        </div>
      )}
      <ConfirmBox
        title={"Start Recording"}
        subTitle={"Are you sure you want to start recording?"}
        open={showConfirmationPopup}
        successText={"Yes"}
        onSuccess={() => {
          _handleStartRecording();
          setShowConfirmationPopup(false);
        }}
        rejectText={"No"}
        onReject={() => {
          setShowConfirmationPopup(false);
        }}
      />
    </>
  );
};
const GoLiveBTN = ({ isMobile, isTab }) => {
  const mMeeting = useMeeting({});

  const [isPopupShown, setIsPopupShown] = useState(false);

  const startLivestream = mMeeting?.startLivestream;
  const stopLivestream = mMeeting?.stopLivestream;
  const livestreamState = mMeeting?.livestreamState;

  const isLiveStreaming = useIsLivestreaming();

  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        livestreamState === Constants.livestreamEvents.LIVESTREAM_STARTING ||
        livestreamState === Constants.livestreamEvents.LIVESTREAM_STOPPING,
    }),
    [livestreamState]
  );

  const {
    participantCanToggleLivestream,
    liveStreamConfig,
    setSideBarMode,
    setSideBarNestedMode,
    appMeetingLayout,
    liveStreamTheme,
  } = useMeetingAppContext();

  const { type, priority, gridSize } = useMemo(
    () => ({
      type: appMeetingLayout.type,
      priority: appMeetingLayout.priority,
      gridSize: appMeetingLayout.gridSize,
    }),
    [appMeetingLayout]
  );

  const typeRef = useRef(type);
  const priorityRef = useRef(priority);
  const gridSizeRef = useRef(gridSize);
  const isLiveStreamingRef = useRef(isLiveStreaming);
  const liveStreamConfigRef = useRef(liveStreamConfig);

  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  useEffect(() => {
    priorityRef.current = priority;
  }, [priority]);

  useEffect(() => {
    gridSizeRef.current = gridSize;
  }, [gridSize]);

  useEffect(() => {
    isLiveStreamingRef.current = isLiveStreaming;
  }, [isLiveStreaming]);

  useEffect(() => {
    liveStreamConfigRef.current = liveStreamConfig;
  }, [liveStreamConfig]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: liveBlink,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
    height: 64,
    width: 170,
  };

  const _handleStartLivestream = () => {
    const type = typeRef.current;
    const priority = priorityRef.current;
    const gridSize = gridSizeRef.current;

    const layout = { type, priority, gridSize };

    startLivestream(liveStreamConfig, { layout, theme: liveStreamTheme });
  };

  const _handleClick = () => {
    const isLiveStreaming = isLiveStreamingRef.current;

    if (isLiveStreaming) {
      stopLivestream();
    } else {
      if (liveStreamConfigRef.current.length > 0) {
        _handleStartLivestream();
      } else {
        setIsPopupShown(true);
      }
    }
  };

  return (
    <>
      {isMobile || isTab ? (
        <MobileIconButton
          bgColor={"#D32F2F"}
          onClick={_handleClick}
          tooltipTitle={
            livestreamState === Constants.livestreamEvents.LIVESTREAM_STARTED
              ? "Stop Live"
              : livestreamState ===
                Constants.livestreamEvents.LIVESTREAM_STARTING
                ? "Starting Livestream"
                : livestreamState ===
                  Constants.livestreamEvents.LIVESTREAM_STOPPED
                  ? "Go Live"
                  : livestreamState ===
                    Constants.livestreamEvents.LIVESTREAM_STOPPING
                    ? "Stopping Livestream"
                    : "Go Live"
          }
          Icon={LiveIcon}
          buttonText={
            livestreamState === Constants.livestreamEvents.LIVESTREAM_STARTED
              ? "Stop Live"
              : livestreamState ===
                Constants.livestreamEvents.LIVESTREAM_STARTING
                ? "Starting Livestream"
                : livestreamState ===
                  Constants.livestreamEvents.LIVESTREAM_STOPPED
                  ? "Go Live"
                  : livestreamState ===
                    Constants.livestreamEvents.LIVESTREAM_STOPPING
                    ? "Stopping Livestream"
                    : "Go Live"
          }
          isFocused={isLiveStreaming}
          lottieOption={isLiveStreaming ? defaultOptions : null}
          disabled={!participantCanToggleLivestream}
          isRequestProcessing={isRequestProcessing}
        />
      ) : (
        <OutlineIconTextButton
          bgColor={"#D32F2F"}
          onClick={_handleClick}
          tooltipTitle={
            livestreamState === Constants.livestreamEvents.LIVESTREAM_STARTED
              ? "Stop Live"
              : livestreamState ===
                Constants.livestreamEvents.LIVESTREAM_STARTING
                ? "Starting Livestream"
                : livestreamState ===
                  Constants.livestreamEvents.LIVESTREAM_STOPPED
                  ? "Go Live"
                  : livestreamState ===
                    Constants.livestreamEvents.LIVESTREAM_STOPPING
                    ? "Stopping Livestream"
                    : "Go Live"
          }
          buttonText="Go Live"
          textColor="#fff"
          lottieOption={isLiveStreaming ? defaultOptions : null}
          disabled={!participantCanToggleLivestream}
          isRequestProcessing={isRequestProcessing}
        />
      )}
      <ConfirmBox
        open={isPopupShown}
        title={"Add live stream configuration"}
        subTitle={
          "Please add live stream configuration to start live streaming"
        }
        successText={"Proceed"}
        onSuccess={() => {
          setSideBarMode((s) => sideBarModes.ACTIVITIES);
          setSideBarNestedMode((s) => sideBarNestedModes.ADD_LIVE_STREAM);
          setIsPopupShown(false);
        }}
        rejectText={"Cancel"}
        onReject={() => {
          setIsPopupShown(false);
        }}
      />
    </>
  );
};
const HlsBTN = ({ isMobile, isTab }) => {
  const mMeeting = useMeeting({});
  const theme = useTheme();

  const startHls = mMeeting?.startHls;
  const stopHls = mMeeting?.stopHls;
  const hlsState = mMeeting?.hlsState;

  const isHls = useIsHls();

  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        hlsState === Constants.hlsEvents.HLS_STARTING ||
        hlsState === Constants.hlsEvents.HLS_STOPPING,
    }),
    [hlsState]
  );

  const { appMeetingLayout, participantCanToggleHls, hlsTheme, appTheme } =
    useMeetingAppContext();

  const { type, priority, gridSize } = useMemo(
    () => ({
      type: appMeetingLayout.type,
      priority: appMeetingLayout.priority,
      gridSize: appMeetingLayout.gridSize,
    }),
    [appMeetingLayout]
  );

  const typeRef = useRef(type);
  const priorityRef = useRef(priority);
  const gridSizeRef = useRef(gridSize);
  const isHlsRef = useRef(isHls);

  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  useEffect(() => {
    priorityRef.current = priority;
  }, [priority]);

  useEffect(() => {
    gridSizeRef.current = gridSize;
  }, [gridSize]);

  useEffect(() => {
    isHlsRef.current = isHls;
  }, [isHls]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: liveHLS,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
    height: 64,
    width: 170,
  };

  const defaultOptionsStoppingHls = {
    loop: true,
    autoplay: true,
    animationData: stoppingHLS,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
    height: 64,
    width: 170,
  };

  const _handleStartHLS = () => {
    const type = typeRef.current;
    const priority = priorityRef.current;
    const gridSize = gridSizeRef.current;

    const layout = { type, priority, gridSize };

    startHls({ layout, theme: hlsTheme });
  };

  const _handleClick = () => {
    const isHls = isHlsRef.current;

    if (isHls) {
      stopHls();
    } else {
      _handleStartHLS();
    }
  };

  return isMobile || isTab ? (
    <MobileIconButton
      onClick={_handleClick}
      focusBGColor={
        appTheme === appThemes.LIGHT && "#95959E"
      }
      tooltipTitle={
        hlsState === Constants.hlsEvents.HLS_STARTED ||
          hlsState === Constants.hlsEvents.HLS_PLAYABLE
          ? "Stop HLS"
          : hlsState === Constants.hlsEvents.HLS_STARTING
            ? "Starting HLS"
            : hlsState === Constants.hlsEvents.HLS_STOPPED
              ? "Start HLS"
              : hlsState === Constants.hlsEvents.HLS_STOPPING
                ? "Stopping HLS"
                : "Start HLS"
      }
      Icon={LiveIcon}
      buttonText={
        hlsState === Constants.hlsEvents.HLS_STARTED ||
          hlsState === Constants.hlsEvents.HLS_PLAYABLE
          ? "Stop HLS"
          : hlsState === Constants.hlsEvents.HLS_STARTING
            ? "Starting HLS"
            : hlsState === Constants.hlsEvents.HLS_STOPPED
              ? "Start HLS"
              : hlsState === Constants.hlsEvents.HLS_STOPPING
                ? "Stopping HLS"
                : "Start HLS"
      }
      bgColor={
        appTheme === appThemes.LIGHT &&
        (hlsState === Constants.hlsEvents.HLS_STARTED ||
          hlsState === Constants.hlsEvents.HLS_STOPPING) &&
        "#EEF0F2"
      }
      isFocused={isHls}
      disabled={!participantCanToggleHls}
      lottieOption={isHls ? defaultOptions : null}
      isRequestProcessing={isRequestProcessing}
    />
  ) : (
    <OutlineIconTextButton
      onClick={_handleClick}
      tooltipTitle={
        hlsState === Constants.hlsEvents.HLS_STARTED ||
          hlsState === Constants.hlsEvents.HLS_PLAYABLE
          ? "Stop HLS"
          : hlsState === Constants.hlsEvents.HLS_STARTING
            ? "Starting HLS"
            : hlsState === Constants.hlsEvents.HLS_STOPPED
              ? "Start HLS"
              : hlsState === Constants.hlsEvents.HLS_STOPPING
                ? "Stopping HLS"
                : "Start HLS"
      }
      buttonText={
        hlsState === Constants.hlsEvents.HLS_STARTED ||
          hlsState === Constants.hlsEvents.HLS_PLAYABLE
          ? "Stop HLS"
          : hlsState === Constants.hlsEvents.HLS_STARTING
            ? "Starting HLS"
            : hlsState === Constants.hlsEvents.HLS_STOPPED
              ? "Start HLS"
              : hlsState === Constants.hlsEvents.HLS_STOPPING
                ? "Stopping HLS"
                : "Start HLS"
      }
      lottieOption={isHls ? defaultOptions : null}
      disabled={!participantCanToggleHls}
      isRequestProcessing={isRequestProcessing}
    />
  );
};

const SingleMicMenu = ({
  micArr,
  Icon,
  label,
  // classes,
  selectMicDeviceId,
  setSelectMicDeviceId,
  isOutputMics,
  changeMic,
  appTheme,
  theme,
  handleClose,
}) => {
  const BoxElement =
    appTheme === appThemes.LIGHT
      ? CustomBoxLight
      : appTheme === appThemes.DARK
        ? CustomBox
        : CustomBoxDefault;

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          padding: '1.2vh',
          paddingBottom: 0,
        }}
      >
        <Icon 
          style={{
            width: '1.8vh',
            height: '1.8vh',
          }}
        />

        <Typography
          style={{
            marginLeft: '0.6vh',
            fontSize: '1.4vh',
            color: "#95959E",
          }}
        >
          {label}
        </Typography>
      </Box>
      <MenuList
        disableRipple
        disableFocusRipple
        style={{
          backgroundColor:
            appTheme === appThemes.DARK
              ? theme.palette.darkTheme.slightLighter
              : appTheme === appThemes.LIGHT
                ? theme.palette.lightTheme.two
                : "",
          color:
            appTheme === appThemes.DARK
              ? theme.palette.common.white
              : appTheme === appThemes.LIGHT
                ? "#95959E"
                : "",
        }}
      >
        {micArr.map(({ deviceId, label }, index) => (
          <BoxElement
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: '1.2vh',
              paddingRight: '1.2vh',
              backgroundColor:
                deviceId === selectMicDeviceId
                  ? appTheme === appThemes.DARK
                    ? "#3F4046"
                    : appTheme === appThemes.LIGHT
                      ? theme.palette.lightTheme.three
                      : "#6D6E71"
                  : "",
            }}
          >
            {deviceId === selectMicDeviceId && (
              <SelectedIcon 
                style={{
                  width: '1.6vh',
                  height: '1.6vh',
                  marginRight: '0.6vh',
                }}
              />
            )}

            <CustomMenuItem
              disableRipple
              style={{
                display: "flex",
                flex: 1,
                fontSize: '1.3vh',
                backgroundColor:
                  deviceId === selectMicDeviceId
                    ? appTheme === appThemes.DARK
                      ? "#3F4046"
                      : appTheme === appThemes.LIGHT
                        ? theme.palette.lightTheme.three
                        : "#6D6E71"
                    : "",
              }}
              key={`mics_${deviceId}`}
              selected={deviceId === selectMicDeviceId}
              onClick={() => {
                handleClose();
                setSelectMicDeviceId(deviceId);
                if (!isOutputMics) {
                  changeMic(deviceId);
                }
              }}
            >
              {label || `Mic ${index + 1}`}
            </CustomMenuItem>
          </BoxElement>
        ))}
      </MenuList>
    </Box>
  );
};

const MicMenu = ({
  selectMicDeviceId,
  setSelectMicDeviceId,
  selectedOutputDeviceId,
  setSelectedOutputDeviceId,
  localMicOn,
  downArrow,
  mics,
  outputmics,
  // classes,
  _handleNoiseClick,
  handleClose,
  isNoiseRemovalChecked,
  theme,
  appTheme,
  tollTipEl,
  changeMic,
}) => {
  const BoxElement =
    appTheme === appThemes.LIGHT
      ? CustomBoxLight
      : appTheme === appThemes.DARK
        ? CustomBox
        : CustomBoxDefault;
  return (
    <Popover
      container={tollTipEl.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      anchorEl={tollTipEl.current}
      open={Boolean(downArrow)}
      onClose={handleClose}
    >
      <Box
        style={{
          backgroundColor:
            appTheme === appThemes.DARK
              ? theme.palette.darkTheme.slightLighter
              : appTheme === appThemes.LIGHT
                ? theme.palette.lightTheme.two
                : "",
        }}
      >
        <SingleMicMenu
          micArr={mics}
          label={"MICROPHONE"}
          Icon={MicrophoneIcon}
          selectMicDeviceId={selectMicDeviceId}
          setSelectMicDeviceId={setSelectMicDeviceId}
          changeMic={changeMic}
          // classes={classes}
          appTheme={appTheme}
          theme={theme}
          handleClose={handleClose}
        />
        <Box
          style={{
            height: 1,
            width: "100%",
            borderTop: `1px solid #95959E`,
          }}
        ></Box>

        <SingleMicMenu
          micArr={outputmics}
          label={"SPEAKER"}
          Icon={SpeakerMenuIcon}
          selectMicDeviceId={selectedOutputDeviceId}
          setSelectMicDeviceId={setSelectedOutputDeviceId}
          isOutputMics={true}
          // classes={classes}
          changeMic={changeMic}
          appTheme={appTheme}
          theme={theme}
          handleClose={handleClose}
        />

        {localMicOn && (
          <>
            <Box
              style={{
                height: 1,
                width: "100%",
                borderTop: "1px solid #9FA0A7",
              }}
            ></Box>
            <Box>
              <MenuList
                disableRipple
                disableFocusRipple
                style={{
                  backgroundColor:
                    appTheme === appThemes.DARK
                      ? theme.palette.darkTheme.slightLighter
                      : appTheme === appThemes.LIGHT
                        ? theme.palette.lightTheme.two
                        : "",
                  color:
                    appTheme === appThemes.DARK
                      ? theme.palette.common.white
                      : appTheme === appThemes.LIGHT
                        ? "#95959E"
                        : "",
                }}
              >
                <BoxElement
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: isNoiseRemovalChecked ? '1.2vh' : '0.6vh',
                    paddingRight: '0.6vh',
                    backgroundColor: isNoiseRemovalChecked
                      ? appTheme === appThemes.DARK
                        ? "#3F4046"
                        : appTheme === appThemes.LIGHT
                          ? theme.palette.lightTheme.three
                          : "#6D6E71"
                      : "",
                  }}
                // classes={{
                //   root:
                //     appTheme === appThemes.LIGHT
                //       ? classes.popoverHover
                //       : appTheme === appThemes.DARK
                //       ? classes.popoverHoverDark
                //       : classes.popoverHoverDefault,
                // }}
                >
                  {isNoiseRemovalChecked ? (
                    <SelectedIcon 
                      style={{
                        width: '1.6vh',
                        height: '1.6vh',
                        marginRight: '0.6vh',
                      }}
                    />
                  ) : (
                    <BpCheckbox
                      value={isNoiseRemovalChecked}
                      checked={isNoiseRemovalChecked}
                      onClick={(e) => {
                        _handleNoiseClick({ e, selectMicDeviceId });
                      }}
                    />
                  )}

                  <MenuItem
                    disableRipple
                    style={{
                      display: "flex",
                      flex: 1,
                      fontSize: '1.3vh',
                      backgroundColor: isNoiseRemovalChecked
                        ? appTheme === appThemes.DARK
                          ? "#3F4046"
                          : appTheme === appThemes.LIGHT
                            ? theme.palette.lightTheme.three
                            : "#6D6E71"
                        : "",
                    }}
                    key={`noise_removal`}
                    selected={isNoiseRemovalChecked}
                    onClick={(e) => {
                      handleClose();
                      _handleNoiseClick({ e, selectMicDeviceId });
                    }}
                  // classes={{
                  //   root:
                  //     appTheme === appThemes.LIGHT
                  //       ? classes.menuItemHover
                  //       : appTheme === appThemes.DARK
                  //       ? classes.menuItemDark
                  //       : classes.menuItemDefault,
                  //   gutters: isNoiseRemovalChecked
                  //     ? classes.singleMenuItemGuttersAfterSelect
                  //     : classes.singleMenuItemGutters,
                  // }}
                  >
                    AI Noise Removal
                  </MenuItem>
                </BoxElement>
              </MenuList>
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
}

const MirrorView = ({
  isMirrorViewChecked,
  _handleMirrorClick,
  localWebcamOn,
  handleClose,
  appTheme,
  theme,
}) => {
  // const classes = useStyles();

  const BoxElement =
    appTheme === appThemes.LIGHT
      ? CustomBoxLight
      : appTheme === appThemes.DARK
        ? CustomBox
        : CustomBoxDefault;

  return (
    localWebcamOn && (
      <>
        <Box
          style={{
            height: 1,
            width: "100%",
            borderTop: "1px solid #9FA0A7",
          }}
        ></Box>
        <Box>
          <MenuList
            disableRipple
            disableFocusRipple
            style={{
              backgroundColor:
                appTheme === appThemes.DARK
                  ? theme.palette.darkTheme.slightLighter
                  : appTheme === appThemes.LIGHT
                    ? theme.palette.lightTheme.two
                    : "",
              color:
                appTheme === appThemes.DARK
                  ? theme.palette.common.white
                  : appTheme === appThemes.LIGHT
                    ? "#95959E"
                    : "",
            }}
          >
            <BoxElement
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: isMirrorViewChecked ? 12 : 6,
                paddingRight: 6,
                backgroundColor: isMirrorViewChecked
                  ? appTheme === appThemes.DARK
                    ? "#3F4046"
                    : appTheme === appThemes.LIGHT
                      ? theme.palette.lightTheme.three
                      : "#6D6E71"
                  : "",
              }}
            // classes={{
            //   root:
            //     appTheme === appThemes.LIGHT
            //       ? classes.popoverHover
            //       : appTheme === appThemes.DARK
            //       ? classes.popoverHoverDark
            //       : classes.popoverHoverDefault,
            // }}
            >
              {isMirrorViewChecked ? (
                <SelectedIcon 
                  style={{
                    width: '1.6vh',
                    height: '1.6vh',
                    marginRight: '0.6vh',
                  }}
                />
              ) : (
                <BpCheckbox
                  value={isMirrorViewChecked}
                  checked={isMirrorViewChecked}
                  onClick={(e) => {
                    _handleMirrorClick({ e });
                  }}
                />
              )}

              <CustomMenuItem
                disableRipple
                style={{
                  display: "flex",
                  flex: 1,
                  backgroundColor: isMirrorViewChecked
                    ? appTheme === appThemes.DARK
                      ? "#3F4046"
                      : appTheme === appThemes.LIGHT
                        ? theme.palette.lightTheme.three
                        : "#6D6E71"
                    : "",
                }}
                key={`mirror_view`}
                selected={isMirrorViewChecked}
                onClick={(e) => {
                  handleClose();
                  _handleMirrorClick({ e });
                }}
              // classes={{
              //   root:
              //     appTheme === appThemes.LIGHT
              //       ? classes.menuItemHover
              //       : appTheme === appThemes.DARK
              //       ? classes.menuItemDark
              //       : classes.menuItemDefault,
              //   gutters: isMirrorViewChecked
              //     ? classes.singleMenuItemGuttersAfterSelect
              //     : classes.singleMenuItemGutters,
              // }}
              >
                Mirror View
              </CustomMenuItem>
            </BoxElement>
          </MenuList>
        </Box>
      </>
    )
  );
};

const WebcamBTN = () => {
  const theme = useTheme();
  // const classes = useStyles();
  const mMeeting = useMeeting({});
  const {
    appTheme,
    selectWebcamDeviceId,
    setSelectWebcamDeviceId,
    notificationSoundEnabled,
    notificationAlertsEnabled,
    isMirrorViewChecked,
    setIsMirrorViewChecked,
    cameraId,
  } = useMeetingAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const { getCustomVideoTrack } = useCustomTrack();

  const [downArrow, setDownArrow] = useState(null);
  const [webcams, setWebcams] = useState([]);

  const localWebcamOn = mMeeting?.localWebcamOn;
  const isChangingWebcamRef = useRef(false);
  const toggleWebcam = async () => {
    let track;
    if (!localWebcamOn)
      track = await getCustomVideoTrack(
        cameraId === selectWebcamDeviceId ? cameraId : selectWebcamDeviceId
      );
    mMeeting?.toggleWebcam(track);
  };
  const changeWebcam = async (deviceId) => {
    if (isChangingWebcamRef.current) return;
    isChangingWebcamRef.current = true;

    try {
      const track = await getCustomVideoTrack(deviceId);

      if (track) {
        await mMeeting?.changeWebcam(track);
      } else {
        console.error("Failed to get track for deviceId:", deviceId);
      }
    } catch (error) {
      console.error("Error while changing webcam:", error);
    } finally {
      isChangingWebcamRef.current = false;
    }
  };

  const handleClick = (event) => {
    setDownArrow(event.currentTarget);
  };

  const handleClose = () => {
    setDownArrow(null);
  };

  const getWebcams = async (mGetWebcams) => {
    const webcams = await mGetWebcams();

    webcams && webcams?.length && setWebcams(webcams);
  };

  const tollTipEl = useRef();

  const _handleMirrorClick = async ({ e }) => {
    e.stopPropagation();
    let _isMirrorViewChecked;

    setIsMirrorViewChecked((s) => {
      const notS = !s;
      _isMirrorViewChecked = notS;
      return notS;
    });

    if (_isMirrorViewChecked) {
      // if (notificationSoundEnabled) {
      //   new Audio(
      //     `https://static.videosdk.live/prebuilt/notification.mp3`
      //   ).play();
      // }

      if (notificationAlertsEnabled) {
        enqueueSnackbar("Camera mirror view enabled.");
      }
    }
  };

  const BoxElement =
    appTheme === appThemes.LIGHT
      ? CustomBoxLight
      : appTheme === appThemes.DARK
        ? CustomBox
        : CustomBoxDefault;

  return (
    <Box
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      ref={tollTipEl}
    >
      <OutlineIconButton
        btnID={"btnWebcam"}
        tooltipTitle={localWebcamOn ? "Turn off webcam" : "Turn on webcam"}
        isFocused={localWebcamOn}
        Icon={localWebcamOn ? VideocamRoundedIcon : VideocamOffRoundedIcon}
        onClick={() => {
          toggleWebcam();
        }}
        focusBGColor={
          appTheme === appThemes.LIGHT
            ? "#95959E"
            : "#ffffff33"
        }
        focusIconColor={theme.palette.common.white}
        renderRightComponent={() => {
          return (
            <Tooltip placement="bottom" title={"Change webcam"}>
              <CustomIconButton
                onClick={(e) => {
                  getWebcams(mMeeting?.getWebcams);
                  handleClick(e);
                }}
                size={"small"}
              >
                <ArrowDropDownIcon
                  style={{
                    color: localWebcamOn
                      ? "white"
                      : appTheme === appThemes.LIGHT
                        ? "#95959E"
                        : "white",
                    fontSize: '1.8vh',
                  }}
                />
              </CustomIconButton>
            </Tooltip>
          );
        }}
      />
      <Popover
        container={tollTipEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        anchorEl={tollTipEl.current}
        open={Boolean(downArrow)}
        onClose={handleClose}
      >
        <MenuList
          style={{
            backgroundColor:
              appTheme === appThemes.DARK
                ? theme.palette.darkTheme.slightLighter
                : appTheme === appThemes.LIGHT
                  ? theme.palette.lightTheme.two
                  : "",
            color:
              appTheme === appThemes.DARK
                ? theme.palette.common.white
                : appTheme === appThemes.LIGHT
                  ? "#95959E"
                  : "",
          }}
        >
          {webcams.map(({ deviceId, label }, index) => (
            <BoxElement
              style={{
                display: "flex",
                alignItems: "center",

                backgroundColor:
                  deviceId === selectWebcamDeviceId
                    ? appTheme === appThemes.DARK
                      ? "#3F4046"
                      : appTheme === appThemes.LIGHT
                        ? theme.palette.lightTheme.three
                        : "#6D6E71"
                    : "",
              }}
            >
              <CustomWebcamMenuItem
                key={`output_webcams_${deviceId}`}
                selected={deviceId === selectWebcamDeviceId}
                onClick={() => {
                  handleClose();
                  setSelectWebcamDeviceId(deviceId);
                  changeWebcam(deviceId);
                }}
              // classes={{
              //   root:
              //     appTheme === appThemes.LIGHT
              //       ? classes.popoverHover
              //       : appTheme === appThemes.DARK
              //       ? classes.popoverHoverDark
              //       : "",
              // }}
              >
                {label || `Webcam ${index + 1}`}
              </CustomWebcamMenuItem>
            </BoxElement>
          ))}
        </MenuList>

        <MirrorView
          isMirrorViewChecked={isMirrorViewChecked}
          localWebcamOn={localWebcamOn}
          _handleMirrorClick={_handleMirrorClick}
          handleClose={handleClose}
          appTheme={appTheme}
          theme={theme}
        />
      </Popover>
    </Box>
  );
};
const MicBTN = () => {
  const {
    appTheme,
    notificationSoundEnabled,
    notificationAlertsEnabled,
    selectMicDeviceId,
    setSelectMicDeviceId,
    selectedOutputDeviceId,
    setSelectedOutputDeviceId,
  } = useMeetingAppContext();

  const [isNoiseRemovalChecked, setIsNoiseRemovalChecked] = useState(false);
  const [downArrow, setDownArrow] = useState(null);
  const [mics, setMics] = useState([]);
  const [outputmics, setOutputMics] = useState([]);
  const mMeeting = useMeeting({});
  const theme = useTheme();
  // const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { getCustomAudioTrack } = useCustomTrack();
  const { getPlaybackDevices } = useMediaDevice({ onDeviceChanged });

  const getSpeakers = async () => {
    const devices = await getPlaybackDevices();
    const outputMics = devices.filter(
      (d) => d.deviceId !== "default" && d.deviceId !== "communications"
    );

    outputMics && outputMics?.length && setOutputMics(outputMics);
  };

  function onDeviceChanged() {
    getSpeakers();
  }

  const handleClick = (event) => {
    setDownArrow(event.currentTarget);
  };

  const handleClose = () => {
    setDownArrow(null);
  };

  const localMicOn = mMeeting?.localMicOn;
  const toggleMic = async () => {
    let track;
    if (!localMicOn) track = await getCustomAudioTrack(selectMicDeviceId);
    mMeeting?.toggleMic(track);
  };
  const changeMic = mMeeting?.changeMic;

  const getMics = async (mGetMics) => {
    const mics = await mGetMics();

    mics && mics?.length && setMics(mics);
  };

  const tollTipEl = useRef();

  const getOutputDevices = async () => {
    await getSpeakers();
  };

  const _handleNoiseClick = async ({ e, selectMicDeviceId }) => {
    e.stopPropagation();
    let _isNoiseRemovalChecked;
    setIsNoiseRemovalChecked((s) => {
      const notS = !s;
      _isNoiseRemovalChecked = notS;
      return notS;
    });

    try {
      const processor = new VideoSDKNoiseSuppressor();

      const stream = await getCustomAudioTrack(selectMicDeviceId);
      const processedStream = await processor.getNoiseSuppressedAudioStream(
        stream
      );

      changeMic(processedStream);
    } catch (error) {
      console.log(error);
    }

    if (_isNoiseRemovalChecked) {
      // if (notificationSoundEnabled) {
      //   new Audio(
      //     `https://static.videosdk.live/prebuilt/notification.mp3`
      //   ).play();
      // }

      if (notificationAlertsEnabled) {
        enqueueSnackbar("Noise removal activated");
      }
    }
  };

  return (
    <Box
      ref={tollTipEl}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <OutlineIconButton
        btnID={"btnMic"}
        tooltipTitle={
          isNoiseRemovalChecked
            ? "Noise Removal Activated"
            : localMicOn
              ? "Turn off mic"
              : "Turn on mic"
        }
        isFocused={localMicOn}
        Icon={localMicOn ? MicOn : MicOffIcon}
        onClick={toggleMic}
        focusBGColor={
          appTheme === appThemes.LIGHT
            ? "#95959E"
            : "#ffffff33"
        }
        focusBorderColor={isNoiseRemovalChecked && localMicOn ? "#7dcc7d" : ""}
        focusIconColor={theme.palette.common.white}
        renderRightComponent={() => {
          return (
            <Tooltip placement="bottom" title={"Change microphone"}>
              <CustomIconButton
                p={0}
                onClick={(e) => {
                  getMics(mMeeting.getMics);
                  getOutputDevices();
                  handleClick(e);
                }}
                size={"small"}
              >
                <ArrowDropDownIcon
                  style={{
                    color: localMicOn
                      ? "white"
                      : appTheme === appThemes.LIGHT
                        ? "#95959E"
                        : "white",
                    fontSize: '1.8vh',
                  }}
                />
              </CustomIconButton>
            </Tooltip>
          );
        }}
      />
      <MicMenu
        selectMicDeviceId={selectMicDeviceId}
        setSelectMicDeviceId={setSelectMicDeviceId}
        selectedOutputDeviceId={selectedOutputDeviceId}
        setSelectedOutputDeviceId={setSelectedOutputDeviceId}
        isNoiseRemovalChecked={isNoiseRemovalChecked}
        localMicOn={localMicOn}
        theme={theme}
        appTheme={appTheme}
        downArrow={downArrow}
        tollTipEl={tollTipEl}
        changeMic={changeMic}
        mics={mics}
        outputmics={outputmics}
        // classes={classes}
        _handleNoiseClick={_handleNoiseClick}
        handleClose={handleClose}
      />
    </Box>
  );
};
const EndCallBTN = () => {
  const mMeeting = useMeeting({});
  // const classes = useStyles();

  const [isEndMeeting, setIsEndMeeting] = useState(false);
  const {
    endCallContainerRef,
    participantCanEndMeeting,
    participantCanLeave,
    meetingMode,
    appTheme,
  } = useMeetingAppContext();

  const sendChatMessage = mMeeting?.sendChatMessage;

  const leave = mMeeting?.leave;
  const end = mMeeting?.end;

  const tollTipEl = useRef();

  const theme = useTheme();

  const [downArrow, setDownArrow] = useState(null);

  const handleClick = (event) => {
    setDownArrow(event.currentTarget);
  };

  const handleClose = () => {
    setDownArrow(null);
  };

  return (
    <Box
      ref={tollTipEl}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <OutlineIconButton
        ref={endCallContainerRef}
        tooltipTitle={
          !participantCanLeave && meetingMode === meetingModes.CONFERENCE
            ? "End Call"
            : participantCanEndMeeting &&
              meetingMode === meetingModes.CONFERENCE
              ? "End session"
              : "Leave Call"
        }
        bgColor={theme.palette.error.main}
        color={theme.palette.common.white}
        Icon={EndCall}
        onClick={(e) => {
          window.onbeforeunload = null;
          !participantCanLeave && meetingMode === meetingModes.CONFERENCE
            ? setIsEndMeeting(true)
            : participantCanEndMeeting &&
              meetingMode === meetingModes.CONFERENCE
              ? handleClick(e)
              : leave();
        }}
      />
      {participantCanEndMeeting && (
        <>
          <Popover
            container={tollTipEl.current}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            anchorEl={tollTipEl.current}
            open={Boolean(downArrow)}
            onClose={handleClose}
          // classes={{
          //   paper: classes.popoverBorder,
          // }}
          >
            <MenuList
              style={{
                backgroundColor:
                  appTheme === appThemes.DARK
                    ? theme.palette.darkTheme.slightLighter
                    : appTheme === appThemes.LIGHT
                      ? theme.palette.lightTheme.two
                      : "",
                color:
                  appTheme === appThemes.DARK
                    ? theme.palette.common.white
                    : appTheme === appThemes.LIGHT
                      ? "#95959E"
                      : "",
              }}
            >
              <MenuItem
                key={`leave`}
                onClick={() => {
                  window.onbeforeunload = null;
                  localStorage.removeItem('selectedStageOverlayID');
                  localStorage.removeItem('selectedStageBackgroundID');
                  localStorage.removeItem('selectedBackgroundId');
                  localStorage.removeItem('selectedLogoId');
                  localStorage.removeItem('bgColor');
                  localStorage.removeItem('bannerText');
                  localStorage.removeItem('fontSize');
                  sessionStorage.removeItem('bannerName');
                  leave();
                }}
              // classes={{
              //   root:
              //     appTheme === appThemes.LIGHT
              //       ? classes.popoverHover
              //       : appTheme === appThemes.DARK
              //       ? classes.popoverHoverDark
              //       : "",
              // }}
              >
                <Box style={{ display: "flex", flexDirection: "row" }}>
                  <Box
                    style={{
                      backgroundColor:
                        appTheme === appThemes.DARK
                          ? theme.palette.darkTheme.seven
                          : appTheme === appThemes.LIGHT
                            ? theme.palette.lightTheme.three
                            : theme.palette.common.sidePanel,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "3.89vh",
                      width: "3.89vh",
                      borderRadius: "0.37vh",
                    }}
                  >
                    <LeaveMeetingIcon
                      height="2.4vh"
                      width="2.4vh"
                      fill={
                        appTheme === appThemes.LIGHT &&
                        "#95959E"
                      }
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      flex: 1,
                      flexDirection: "column",
                      marginLeft: "1.11vh",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: "1.6vh",
                        color:
                          appTheme === appThemes.LIGHT &&
                          "#95959E",
                      }}
                    >
                      Leave
                    </Typography>
                    <Typography
                      color={"textSecondary"}
                      style={{
                        fontSize: "1.2vh",
                        color:
                          appTheme === appThemes.DARK
                            ? theme.palette.darkTheme.four
                            : appTheme === appThemes.LIGHT
                              ? theme.palette.lightTheme.five
                              : "",
                      }}
                    >
                      Only you will leave the call.
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem
                style={{ marginTop: "0.37vh" }}
                key={`end`}
                onClick={() => {
                  localStorage.removeItem('selectedStageOverlayID');
                  localStorage.removeItem('selectedStageBackgroundID');
                  localStorage.removeItem('selectedBackgroundId');
                  localStorage.removeItem('bgColor');
                  localStorage.removeItem('bannerText');
                  localStorage.removeItem('fontSize');
                  sessionStorage.removeItem('bannerName');
                  setIsEndMeeting(true);
                }}
              // classes={{
              //   root:
              //     appTheme === appThemes.LIGHT
              //       ? classes.popoverHover
              //       : appTheme === appThemes.DARK
              //       ? classes.popoverHoverDark
              //       : "",
              // }}
              >
                <Box style={{ display: "flex", flexDirection: "row" }}>
                  <Box
                    style={{
                      backgroundColor:
                        appTheme === appThemes.DARK
                          ? theme.palette.darkTheme.seven
                          : appTheme === appThemes.LIGHT
                            ? theme.palette.lightTheme.three
                            : theme.palette.common.sidePanel,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "3.89vh",
                      width: "3.89vh",
                      borderRadius: "0.37vh",
                    }}
                  >
                    <EndCallIcon
                      height="2.4vh"
                      width="2.4vh"
                      fill={
                        appTheme === appThemes.LIGHT &&
                        "#95959E"
                      }
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      marginLeft: "1.11vh",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: "1.6vh",
                        lineHeight: 1.5,
                        color:
                          appTheme === appThemes.LIGHT &&
                          "#95959E",
                      }}
                    >
                      End
                    </Typography>
                    <Typography
                      style={{
                        fontSize: "1.2vh",
                        color:
                          appTheme === appThemes.DARK
                            ? theme.palette.darkTheme.four
                            : appTheme === appThemes.LIGHT
                              ? theme.palette.lightTheme.five
                              : "",
                      }}
                      color={"textSecondary"}
                    >
                      End call for all participants.
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </MenuList>
          </Popover>

          <ConfirmBox
            open={isEndMeeting}
            title={"Are you sure to end this call for everyone?"}
            successText={"End Call"}
            onSuccess={() => {
              sendChatMessage(
                JSON.stringify({ buttonType: "END_CALL", data: {} })
              );
              setTimeout(() => {
                window.onbeforeunload = null;
                end();
              }, 1000);
            }}
            rejectText="Cancel"
            onReject={() => {
              setIsEndMeeting(false);
            }}
          />
        </>
      )}
    </Box>
  );
};

const TopBar = ({ topBarHeight }) => {
  // const classes = useStyles();
  const [open, setOpen] = useState(false);

  const [defaultBrandLogoUrl, setDefaultBrandLogoUrl] = useState(null);

  const {
    chatEnabled,
    screenShareEnabled,
    canChangeLayout,
    participantCanToggleLivestream,
    liveStreamEnabled,
    hlsEnabled,
    pollEnabled,
    whiteboardEnabled,
    participantCanToggleSelfWebcam,
    participantCanToggleSelfMic,
    raiseHandEnabled,
    recordingEnabled,
    realtimeTranscriptionEnabled,
    brandingEnabled,
    brandLogoURL,
    brandName,
    participantCanLeave,
    poweredBy,
    participantCanEndMeeting,
    animationsEnabled,
    meetingMode,
    participantTabPanelEnabled,
    moreOptionsEnabled,
    appTheme,
  } = useMeetingAppContext();

  const handleClickFAB = () => {
    setOpen(true);
  };

  const handleCloseFAB = () => {
    console.log("asdas");
    setOpen(false);
  };

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const theme = useTheme();

  const topBarButtonTypes = useMemo(
    () => ({
      END_CALL: "END_CALL",
      ACTIVITIES: "ACTIVITIES",
      CHAT: "CHAT",
      PARTICIPANTS: "PARTICIPANTS",
      WHITEBOARDICON: "WHITEBOARDICON",
      SCREEN_SHARE: "SCREEN_SHARE",
      WEBCAM: "WEBCAM",
      MIC: "MIC",
      RAISE_HAND: "RAISE_HAND",
      RECORDING: "RECORDING",
      CLOSE_CAPTION: "CLOSE_CAPTION",
      HLS: "HLS",
      WHITEBOARD: "WHITEBOARD",
      ADD_LIVE_STREAM: "ADD_LIVE_STREAM",
      CONFIGURATION: "CONFIGURATION",
      GO_LIVE: "GO_LIVE",
    }),
    []
  );

  const { topBarIcons, firstFourElements, excludeFirstFourElements } =
    useMemo(() => {
      const arr = [];
      const mobileIconArr = [];

      if (participantCanLeave || participantCanEndMeeting) {
        arr.unshift([topBarButtonTypes.END_CALL]);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.END_CALL,
          priority: 1,
        });
      }

      const arrSideBar = [];

      if (canChangeLayout && meetingMode === meetingModes.CONFERENCE) {
        arrSideBar.unshift(topBarButtonTypes.CONFIGURATION);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.CONFIGURATION,
          priority: 7,
        });
      }
      if (chatEnabled) {
        arrSideBar.unshift(topBarButtonTypes.CHAT);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.CHAT,
          priority: 5,
        });
      }
      if (participantTabPanelEnabled) {
        arrSideBar.unshift(topBarButtonTypes.PARTICIPANTS);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.PARTICIPANTS,
          priority: 10,
        });
      }

      if (moreOptionsEnabled) {
        arrSideBar.unshift(topBarButtonTypes.ACTIVITIES);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.ACTIVITIES,
          // priority: 10,
        });
      }

      arr.unshift(arrSideBar);

      const arrMedia = [];
      if (whiteboardEnabled && meetingMode === meetingModes.CONFERENCE) {
        arrMedia.unshift(topBarButtonTypes.WHITEBOARDICON);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.WHITEBOARDICON,
          priority: 11,
        });
      }
      if (screenShareEnabled && meetingMode === meetingModes.CONFERENCE) {
        arrMedia.unshift(topBarButtonTypes.SCREEN_SHARE);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.SCREEN_SHARE,
          priority: 6,
        });
      }
      if (
        participantCanToggleSelfWebcam &&
        meetingMode === meetingModes.CONFERENCE
      ) {
        arrMedia.unshift(topBarButtonTypes.WEBCAM);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.WEBCAM,
          priority: 2,
        });
      }
      if (
        participantCanToggleSelfMic &&
        meetingMode === meetingModes.CONFERENCE
      ) {
        arrMedia.unshift(topBarButtonTypes.MIC);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.MIC,
          priority: 3,
        });
      }

      if (arrMedia.length) {
        arr.unshift(arrMedia);
      }

      const utilsArr = [];

      if (raiseHandEnabled) {
        utilsArr.unshift(topBarButtonTypes.RAISE_HAND);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.RAISE_HAND,
          priority: 13,
        });
      }
      //
      if (
        realtimeTranscriptionEnabled &&
        meetingMode === meetingModes.CONFERENCE
      ) {
        utilsArr.unshift(topBarButtonTypes.CLOSE_CAPTION);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.CLOSE_CAPTION,
          priority: 8,
        });
      }

      if (recordingEnabled && meetingMode === meetingModes.CONFERENCE) {
        utilsArr.unshift(topBarButtonTypes.RECORDING);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.RECORDING,
          priority: 4,
        });
      }

      // if (whiteboardEnabled && meetingMode === meetingModes.CONFERENCE) {
      //   utilsArr.unshift(topBarButtonTypes.WHITEBOARD);
      //   mobileIconArr.unshift({
      //     buttonType: topBarButtonTypes.WHITEBOARD,
      //     priority: 11,
      //   });
      // }

      if (
        liveStreamEnabled &&
        !participantCanToggleLivestream &&
        meetingMode === meetingModes.CONFERENCE
      ) {
        utilsArr.unshift(topBarButtonTypes.GO_LIVE);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.GO_LIVE,
          priority: 9,
        });
      }

      if (hlsEnabled && meetingMode === meetingModes.CONFERENCE) {
        utilsArr.unshift(topBarButtonTypes.HLS);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.HLS,
          priority: 14,
        });
      }

      if (
        participantCanToggleLivestream &&
        liveStreamEnabled &&
        meetingMode === meetingModes.CONFERENCE
      ) {
        //liveStreamIcon
        utilsArr.unshift(topBarButtonTypes.GO_LIVE);
        mobileIconArr.unshift({
          buttonType: topBarButtonTypes.GO_LIVE,
          priority: 9,
        });
        //AddLiveStreamIcon
        // utilsArr.unshift(topBarButtonTypes.ADD_LIVE_STREAM);
        // mobileIconArr.unshift({
        //   buttonType: topBarButtonTypes.ADD_LIVE_STREAM,
        //   priority: 8,
        // });
      }

      if (participantCanToggleLivestream && !liveStreamEnabled) {
      }

      if (utilsArr.length) {
        arr.unshift(utilsArr);
      }

      //sorting mobile icon
      mobileIconArr
        .sort((iconA, iconB) => iconA.priority - iconB.priority)
        .map((icon) => icon.buttonType);

      const firstFourElements = mobileIconArr.slice(0, 4);

      const excludeFirstFourElements = mobileIconArr.slice(4);

      return {
        topBarIcons: arr,
        mobileIcons: mobileIconArr,
        firstFourElements,
        excludeFirstFourElements,
      };
    }, [
      participantCanToggleSelfMic,
      participantCanToggleSelfWebcam,
      screenShareEnabled,
      pollEnabled,
      whiteboardEnabled,
      chatEnabled,
      raiseHandEnabled,
      topBarButtonTypes,
      recordingEnabled,
      realtimeTranscriptionEnabled,
      meetingMode,
    ]);

  const [topBarVisible, setTopBarVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setTopBarVisible(true);
    }, 100);
  }, []);

  // Helper function to render button component based on buttonType
  function renderButtonComponent(buttonType) {
    switch (buttonType) {
      case topBarButtonTypes.RAISE_HAND:
        return <RaiseHandBTN />;
      case topBarButtonTypes.MIC:
        return <MicBTN />;
      case topBarButtonTypes.WEBCAM:
        return <WebcamBTN />;
      case topBarButtonTypes.SCREEN_SHARE:
        return <ScreenShareBTN />;
      case topBarButtonTypes.END_CALL:
        return <EndCallBTN />;
      case topBarButtonTypes.CLOSE_CAPTION:
        return <TranscriptionBTN />;
      case topBarButtonTypes.RECORDING:
        return <RecordingBTN />;
      case topBarButtonTypes.HLS:
        return <HlsBTN />;
      case topBarButtonTypes.GO_LIVE:
        return <GoLiveBTN />;
      case topBarButtonTypes.WHITEBOARDICON:
        return <WhiteBoardBTN />;
      case topBarButtonTypes.ADD_LIVE_STREAM:
        return <AddLiveStreamBTN />;
      default:
        return null;
    }
  }

  return isTab || isMobile ? (
    <Box
      style={{
        height: topBarHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor:
          appTheme === appThemes.DARK
            ? theme.palette.darkTheme.main
            : appTheme === appThemes.LIGHT
              ? theme.palette.lightTheme.main
              : theme.palette.background.default,
      }}
    >
      {firstFourElements.map((icon, i) => {
        return (
          <Box key={`topbar_controls_j_${i}`} ml={i === 0 ? 0 : 1.5}>
            {renderButtonComponent(icon.buttonType)}
          </Box>
        );
      })}

      {excludeFirstFourElements.length >= 1 && (
        <Box ml={2}>
          <OutlineIconButton
            Icon={MoreHorizIcon}
            // Icon={Boolean(anchorEl) ? CloseIcon : MoreHorizIcon}
            // isFocused={anchorEl}
            onClick={handleClickFAB}
          />
        </Box>
      )}

      <SwipeableDrawer
        anchor={"bottom"}
        open={Boolean(open)}
        onClose={handleCloseFAB}
        onOpen={handleClickFAB}
        style={{ paddingBottom: "10vh" }}
      >
        <Grid
          container
          style={{
            backgroundColor:
              appTheme === appThemes.DARK
                ? theme.palette.darkTheme.main
                : appTheme === appThemes.LIGHT
                  ? theme.palette.lightTheme.main
                  : theme.palette.background.default,
          }}
        >
          {excludeFirstFourElements.map((icon, i) => {
            let ButtonComponent = null;
            switch (icon.buttonType) {
              case topBarButtonTypes.RAISE_HAND:
                ButtonComponent = <RaiseHandBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.MIC:
                ButtonComponent = <MicBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.WEBCAM:
                ButtonComponent = <WebcamBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.SCREEN_SHARE:
                ButtonComponent = <ScreenShareBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.END_CALL:
                ButtonComponent = <EndCallBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.CLOSE_CAPTION:
                ButtonComponent = <TranscriptionBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.RECORDING:
                ButtonComponent = <RecordingBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.HLS:
                ButtonComponent = <HlsBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.GO_LIVE:
                ButtonComponent = <GoLiveBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.WHITEBOARDICON:
                ButtonComponent = <WhiteBoardBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.ADD_LIVE_STREAM:
                ButtonComponent = <AddLiveStreamBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              case topBarButtonTypes.CONFIGURATION:
                ButtonComponent = <ConfigBTN onClick={handleCloseFAB} isMobile={isMobile} isTab={isTab} />;
                break;
              // default:
              //   ButtonComponent = null;
            }
            return (
              <Grid
                item
                xs={4}
                sm={3}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {ButtonComponent}
              </Grid>
            );
          })}
        </Grid>
      </SwipeableDrawer>
    </Box>
  ) : (
    <Box
      className="align-center"
      style={{
        height: topBarHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        overflow: "hidden",
        backgroundColor:
          appTheme === appThemes.DARK
            ? theme.palette.darkTheme.main
            : appTheme === appThemes.LIGHT
              ? theme.palette.lightTheme.main
              : theme.palette.background.default,
        borderBottom: `1px solid ${appTheme === appThemes.LIGHT
          ? theme.palette.lightTheme.outlineColor
          : "#ffffff33"
          }`,
        // position: "relative",
        // top: topBarVisible ? 0 : -topBarHeight,
        position: 'absolute',
        bottom: 0,
        transition: `all ${400 * (animationsEnabled ? 1 : 0.5)}ms`,
        transitionTimingFunction: "ease-in-out",
      }}
    >
      <Box
        style={{
          display: "flex",
          height: topBarHeight,
          paddingLeft: theme.spacing(2),
          position: "relative",
          alignItems: "center",
        }}
      >
        {/* {brandingEnabled && (
          <>
            <img
              alt={"App Logo"}
              style={{
                display: "inline-block",
                height: topBarHeight,
              }}
              src={
                defaultBrandLogoUrl ||
                brandLogoURL ||
                `https://static.videosdk.live/prebuilt/videosdk_logo_circle.png`
              }
              onError={() => {
                setDefaultBrandLogoUrl(
                  `https://static.videosdk.live/prebuilt/videosdk_logo_circle.png`
                );
              }}
            />
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
              ml={1}
            >
              <Typography
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  color:
                    appTheme === appThemes.LIGHT
                      ? "#95959E"
                      : "white",
                }}
              >
                {brandName}
              </Typography>
              {poweredBy ? (
                <Typography
                  style={{
                    fontSize: "0.9rem",
                    wordBreak: "break-all",
                    display: "flex",
                    alignItems: "center",
                    color:
                      appTheme === appThemes.LIGHT &&
                      "#95959E",
                  }}
                  color={"textSecondary"}
                >
                  yrect
                </Typography>
              ) : null}
            </Box>
          </>
        )} */}
      </Box>

      <Box
        display={"flex"}
        alignItems={"center"}
        style={{
          padding: '1.2vh',
          gap: '1.2vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {topBarIcons.map((row, i) => {
          return (
            <React.Fragment key={`topbar_controls_i_${i}`}>
              {i !== 0 && (
                <Box
                  style={{
                    backgroundColor:
                      appTheme === appThemes.LIGHT
                        ? theme.palette.lightTheme.outlineColor
                        : "#ffffff33",
                    width: 1,
                    height: `calc(${topBarHeight}px - 1.2vh)`,
                    flex: 1,
                  }}
                />
              )}
              <Box
                display={"flex"}
                alignItems={"center"}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1vh',
                }}
              >
                {row.map((buttonType, j) => {
                  return (
                    <Box key={`topbar_controls_${buttonType}_${j}`}>
                      {renderButtonComponent(buttonType)}
                    </Box>
                  );
                })}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default TopBar;
